"use client";

import {
  motion,
  type MotionValue,
  useScroll,
  useTransform,
  type Variants,
} from "motion/react";
import type { ReactNode } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import type { MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

import { useReveal } from "../../../_lib/use-reveal";

import { DesktopMockup, PhoneMockup } from "./device-mockups";
import { MeshGradient } from "./mesh-gradient";

// =============================================================================

// The panels open from their center, the backdrop settles, then the devices
// rise and their screens light up
const panelVariants = (delay: number): Variants => ({
  hidden: { clipPath: "inset(9% 5% 9% 5% round 1.75rem)" },
  visible: {
    clipPath: "inset(0% 0% 0% 0% round 0.5rem)",
    transition: { duration: 1.4, delay, ease: EASE_OUT_EXPO },
  },
});

const backdropVariants: Variants = {
  hidden: { scale: 1.3 },
  visible: { scale: 1, transition: { duration: 2, ease: EASE_OUT_EXPO } },
};

const deviceVariants = (delay: number): Variants => ({
  hidden: { opacity: 0, y: 70 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.3, delay, ease: EASE_OUT_EXPO },
  },
});

const screenVariants = (delay: number): Variants => ({
  hidden: { opacity: 0, scale: 1.08 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.2, delay, ease: EASE_OUT_EXPO },
  },
});

const desktopScreenVariants = screenVariants(0.8);
const phoneScreenVariants = screenVariants(0.95);

/** The screens light up once their device has risen */
function DesktopScreen({ children }: { children: ReactNode }) {
  return (
    <motion.div variants={desktopScreenVariants} className="absolute inset-0">
      {children}
    </motion.div>
  );
}

function PhoneScreen({ children }: { children: ReactNode }) {
  return (
    <motion.div variants={phoneScreenVariants} className="absolute inset-0">
      {children}
    </motion.div>
  );
}

// =============================================================================

type PanelProps = {
  colors: string[];
  align: "left" | "right";
  delay: number;
  className?: string;
  children: ReactNode;
};

function Panel({ colors, align, delay, className, children }: PanelProps) {
  return (
    <motion.div
      variants={panelVariants(delay)}
      className={cn("relative overflow-hidden rounded-lg", className)}
    >
      <motion.div variants={backdropVariants} className="absolute inset-0">
        <MeshGradient colors={colors} align={align} />
      </motion.div>
      {children}
    </motion.div>
  );
}

function Parallax({
  y,
  className,
  children,
}: {
  y: MotionValue<number>;
  className?: string;
  children: ReactNode;
}) {
  return (
    <motion.div style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

// =============================================================================

type DeviceShowcaseProps = {
  colors: string[];
  desktop: MediaAsset | null;
  mobile: MediaAsset | null;
  labels: { group: string; desktop: string; mobile: string };
};

/**
 * Desktop and phone mockups over the animated gradient of the project. Large
 * screens: a narrow phone panel next to a wide desktop panel (one continuous
 * backdrop). Small screens: one panel, the phone overlapping the desktop.
 */
export function DeviceShowcase({
  colors,
  desktop,
  mobile,
  labels,
}: DeviceShowcaseProps) {
  const { ref, isRevealed } = useReveal<HTMLDivElement>("0px 0px -15% 0px");
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Devices drift at different speeds while scrolling: depth
  const desktopY = useTransform(scrollYProgress, [0, 1], [18, -18]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  if (!desktop && !mobile) return null;

  const phone = mobile && (
    <PhoneMockup
      image={mobile}
      alt={labels.mobile}
      className="h-full"
      Screen={PhoneScreen}
    />
  );

  return (
    <motion.div
      ref={ref}
      role="group"
      aria-label={labels.group}
      initial="hidden"
      animate={isRevealed ? "visible" : "hidden"}
      className={cn(
        "@container grid gap-3 sm:gap-4 md:h-[clamp(24rem,38.5vw,34.625rem)]",
        mobile && desktop && "md:grid-cols-[311fr_1001fr]"
      )}
    >
      {/* Phone panel (large screens) */}
      {mobile && desktop && (
        <Panel
          colors={colors}
          align="left"
          delay={0}
          className="hidden md:block"
        >
          <Parallax
            y={phoneY}
            className="absolute inset-0 flex items-center justify-center"
          >
            <motion.div variants={deviceVariants(0.45)} className="h-[76%]">
              {phone}
            </motion.div>
          </Parallax>
        </Panel>
      )}

      {/* Desktop panel, or the only panel */}
      <Panel
        colors={colors}
        align="right"
        delay={0.08}
        className="aspect-4/3 sm:aspect-16/10 md:aspect-auto"
      >
        {desktop ? (
          <>
            <Parallax
              y={desktopY}
              className={cn(
                "absolute inset-x-0 top-[13%] flex justify-center md:top-[13.9%]",
                mobile && "max-md:translate-x-[-7%]"
              )}
            >
              <motion.div
                variants={deviceVariants(0.3)}
                className="w-[74%] md:w-[min(58.5%,calc(clamp(24rem,38.5vw,34.625rem)*1.05))]"
              >
                <DesktopMockup
                  image={desktop}
                  alt={labels.desktop}
                  Screen={DesktopScreen}
                  eager
                />
              </motion.div>
            </Parallax>
            {/* Phone in front of the desktop (small screens) */}
            {mobile && (
              <Parallax
                y={phoneY}
                className="absolute right-[6%] bottom-[7%] h-[66%] md:hidden"
              >
                <motion.div variants={deviceVariants(0.5)} className="h-full">
                  {phone}
                </motion.div>
              </Parallax>
            )}
          </>
        ) : (
          mobile && (
            <Parallax
              y={phoneY}
              className="absolute inset-0 flex items-center justify-center"
            >
              <motion.div variants={deviceVariants(0.3)} className="h-[78%]">
                {phone}
              </motion.div>
            </Parallax>
          )
        )}
      </Panel>
    </motion.div>
  );
}

// =============================================================================
